<template>
  <div class="slider-container">
    <TextBase :text="label" :type="labelType" weight="bold" />
    <div class="slider-box">
      <div class="value-box">
        <div class="input-field">
          <TextFieldStandard
            :type="'number'"
            :numberValue="parseFloat(fromValue.toFixed(5))"
            @emitInput="(v) => updateValueFrom(v)"
            @emitBlur="inputValueBlur"
            @keydown.enter="inputValueBlur"
          />
        </div>
        <div :style="{ width: '8px' }">-</div>
        <div class="input-field">
          <TextFieldStandard
            :type="'number'"
            :numberValue="parseFloat(toValue.toFixed(5))"
            @emitInput="(v) => updateValueTo(v)"
            @emitBlur="inputValueBlur"
            @keydown.enter="inputValueBlur"
          />
        </div>
      </div>
      <MinMaxRangeSlider
        :from="savedFromValue"
        :to="savedToValue"
        :min="min"
        :max="max"
        :step="step"
        :fromColor="fromColor"
        :toColor="toColor"
        :middleColor="middleColor"
        @updateValueFrom="(v) => updateValueFrom(v, true)"
        @updateValueTo="(v) => updateValueTo(v, true)"
        @changeValue="(v) => emit('changeValue', v)"
      />
    </div>
    <div class="slider-values">
      <span>{{ formatter(min, locale, format) }}</span>
      <span>{{ formatter(max, locale, format) }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue'
import TextFieldStandard from '@/design-system/atoms/TextFieldStandard.vue'
import MinMaxRangeSlider from '@/design-system/molecules/MinMaxRangeSlider.vue'
import TextBase, { TextType } from '../atoms/TextBase.vue'
import { FormatOptions, formatter } from '@/components/AnalyticsDashboard/formatter'
import { useI18n } from 'vue-i18n'

type Props = {
  to: number
  from: number
  min: number
  max: number
  step: number
  fromColor: string
  toColor: string
  middleColor: string
  label: string
  labelType?: TextType
  format?: FormatOptions
}

type Emits = {
  (e: 'changeValue', value: { from: number; to: number }): void
  (e: 'updateValue', value: { from: number; to: number }): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { locale } = useI18n()

const fromValue = ref(props.from)
const toValue = ref(props.to)
const savedFromValue = ref(props.from)
const savedToValue = ref(props.to)

onMounted(() => {
  scrollToBottom()
})

watch(
  [() => props.from, () => props.to],
  ([from, to]) => {
    updateValueFrom(from, true)
    updateValueTo(to, true)
  },
  { immediate: true }
)

watch([savedFromValue, savedToValue], ([from, to]) => {
  emit('updateValue', { from, to })
})

function scrollToBottom() {
  const rangeSlider = document.querySelector('.range-slider')
  if (rangeSlider) {
    rangeSlider.scrollTop = rangeSlider.scrollHeight // Scroll to the bottom of the container
  }
}
function updateValueFrom(number: number | string, save: boolean = false) {
  if (typeof number === 'number') {
    fromValue.value = number
    if (save) {
      savedFromValue.value = number
    }
  }
}
function updateValueTo(number: number | string, save: boolean = false) {
  if (typeof number === 'number') {
    toValue.value = number
    if (save) {
      savedToValue.value = number
    }
  }
}
function inputValueBlur() {
  savedFromValue.value = fromValue.value
  savedToValue.value = toValue.value
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';

.slider-container {
  width: 100%;
}

.label {
  @include font-mixins.desktop-bold-label;
}
.value-box {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
.input-field {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
}
.label-input {
  @include font-mixins.desktop-bold-paragraph;
}

.slider-box {
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.slider-values {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
</style>
